import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchDashboardData } from "../../redux/actions/dashboardActions";
import { logoutUser } from "../../redux/actions/authActions";
import UpdatePasswordModal from "../Customer/UpdatePasswordModal";
import { dealerAccess } from "../../redux/actions/orderActions";
import { PermissionContext } from "../../Context/PermissionContext";

import {
  FiHome,
  FiLogOut,
  FiUser,
  FiBarChart2,
  FiSettings,
  FiSearch,
  FiMenu,
  FiX,
  FiFileText,
  FiGift,
  FiCreditCard,
  FiShoppingCart,
  FiPlusCircle,
  FiImage,
  FiLock,
  FiAlertCircle,
} from "react-icons/fi";
// ✅ STEP 1: alias → menu name map
const aliasToMenuMap = {
  customer_management: "View Customers",
  salesman_management: "Salesmen List",
  redemption_form: "Redemption List",
  qr_design: "QR Designs",
  banners: "Banners",
  gifts: "Gift Masters",
  reports: "Reports",
  slogans: "Slogans",


};
export const DashboardLayout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);
  const dashboardData = useSelector((state) => state.dashboard.data);
  const { access_modules } = useSelector((state) => state.dealerAccess);

  const userDetails = dashboardData || userInfo;
  // const roleId = userInfo?.role_id;
// FIX - fallback chain: userInfo first, then dashboardData
const roleId = userInfo?.role_id || dashboardData?.role_id;


  // ✅ STEP 2: Build menuPermissions for context (used by child pages)
  // const menuPermissions = {};
  // const permData = access_modules?.data ? [...access_modules.data] : [];
  // if (permData.length > 0) {
  //   permData.forEach((mod) => {
  //     const menuName = aliasToMenuMap[mod.alias];
  //     if (menuName) {
  //       menuPermissions[menuName] = mod;
  //     }
  //   });
  // }

  // ✅ STEP 3: Build set of items to HIDE (is_read === false)
  // const hiddenItems = new Set();
  // if (permData.length > 0) {
  //   permData.forEach((mod) => {
  //     if (mod.is_read === false) {
  //       const menuName = aliasToMenuMap[mod.alias];
  //       if (menuName) hiddenItems.add(menuName);
  //     }
  //   });
  // }

  // ✅ Replace the static menuPermissions build with useMemo
  const menuPermissions = React.useMemo(() => {
    const result = {};
    const permData = access_modules?.data ? [...access_modules.data] : [];
    permData.forEach((mod) => {
      const menuName = aliasToMenuMap[mod.alias];
      if (menuName) result[menuName] = mod; // "Slogans" → mod
      result[mod.name] = mod;             // "Slogans" → mod
      result[mod.alias] = mod;             // "slogans" → mod
    });
    return result;
  }, [access_modules]); // ✅ rebuilds whenever access_modules changes

  // ✅ Replace the static hiddenItems build with useMemo
  const hiddenItems = React.useMemo(() => {
    const hidden = new Set();
    const permData = access_modules?.data ? [...access_modules.data] : [];
    permData.forEach((mod) => {
      if (mod.is_read === false) {
        const menuName = aliasToMenuMap[mod.alias];
        if (menuName) hidden.add(menuName);
      }
    });
    return hidden;
  }, [access_modules]);

  // ✅ permissionsLoaded flag
  const permissionsLoaded = !!access_modules?.data;
  console.log("hiddenItems:", [...hiddenItems]); // should show ["QR Designs"]

  // ✅ STEP 4: Static menu lists
  const staticMenuLists = {
    "1": ["Dashboard", "Coupon Orders", "View Coupons statistics", "Salesmen List", "View Customers", "Banners", "Gift Masters", "Reports"],
    "6": ["Dashboard", "Dealers", "Access Modules", "View Order Requests", "Redemption List", "View Coupons statistics", "Reports", "Slogans"],
    "06": ["Dashboard", "Dealers", "Access Modules", "View Order Requests", "Redemption List", "View Coupons statistics", "Reports", "Slogans"],
    "2": ["Dashboard", "View Customers", "Redemption List", "Update Password"],
    "4": ["Dashboard", "Redemption List", "Update Password"],
    default: ["Dashboard", "Coupon Orders", "View Customers"],
  };

  // ✅ STEP 5: Build menuList — filter hidden items RIGHT HERE at build time
  let menuList =
    dashboardData?.menu_list && dashboardData.menu_list.length > 0
      ? dashboardData.menu_list.filter(
        (item) =>
          item !== "Logout" &&
          item !== "Admin Payment Details" &&
          item !== "Gift Allotments" &&
          item !== "Gifts Allotments" &&
          !hiddenItems.has(item) // ✅ removes QR Designs (is_read: false) immediately
      )
      : (staticMenuLists[roleId] || staticMenuLists.default).filter(
        (item) => !hiddenItems.has(item) // ✅ also filters static list
      );

  // ✅ STEP 6: For role 1, ensure Banners and Gift Masters are present (if permitted)
  // if (roleId === "1" || roleId === 1) {
  //   if (!menuList.includes("Banners") && !hiddenItems.has("Banners")) {
  //     const reportsIndex = menuList.indexOf("Reports");
  //     menuList = reportsIndex !== -1
  //       ? [...menuList.slice(0, reportsIndex), "Banners", ...menuList.slice(reportsIndex)]
  //       : [...menuList, "Banners"];
  //   }
  //   if (!menuList.includes("Gift Masters")) {
  //     const reportsIndex = menuList.indexOf("Reports");
  //     menuList = reportsIndex !== -1
  //       ? [...menuList.slice(0, reportsIndex), "Gift Masters", ...menuList.slice(reportsIndex)]
  //       : [...menuList, "Gift Masters"];
  //   }
  // }

  console.log("Final menuList:", [...menuList]); // should NOT contain QR Designs

  // Panel name based on role
  const getPanelName = () => {
    if (roleId === "6" || roleId === "06") return "Admin Panel";
    if (roleId === "1") return userInfo?.firm_name || "Welcome";
    if (roleId === "2") return `Hi, ${userDetails?.name || userInfo?.name || "Salesman"}`;
    if (roleId === "4") return "Customer Panel";
    return userInfo?.firm_name || "Welcome";
  };

  // ✅ Add this right after the menuList is built (after the role 1 Banners/Gift Masters block)

// For role 4 (Customer) with promotional login type, inject "Offers" after "Dashboard"
// if (roleId === "4") {
//   const customerLoginType = dashboardData?.customer_login_type || userInfo?.data?.customer_login_type;
//   const isPromotionalCustomer = customerLoginType === "promotional";
  
//   if (isPromotionalCustomer && !menuList.includes("Gifts Allotments")) {
//     const dashboardIndex = menuList.indexOf("Dashboard");
//     menuList = dashboardIndex !== -1
//       ? [...menuList.slice(0, dashboardIndex + 1), "Gifts Allotments", ...menuList.slice(dashboardIndex + 1)]
//       : [...menuList, "Gifts Allotments"];
//   }
// }
  const firmName = getPanelName();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [showUpdatePasswordModal, setShowUpdatePasswordModal] = useState(false);
  // Add this helper at the top of the file (outside component)
  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch {
      return null;
    }
  };
  // useEffect(() => {
  //   if (!userInfo?.access_token) {
  //     navigate("/login");
  //   } else {
  //     if (!dashboardData) {
  //       dispatch(fetchDashboardData(userInfo.access_token));
  //     }
  //     if (!access_modules?.data) {
  //       dispatch(dealerAccess(userInfo.access_token));
  //     }
  //   }
  // }, [userInfo, navigate, dispatch, dashboardData]); // access_modules excluded intentionally


  // useEffect(() => {
  //   if (!userInfo?.access_token) {
  //     navigate("/login");
  //     return;
  //   }

  //   if (!dashboardData) {
  //     dispatch(fetchDashboardData(userInfo.access_token));
  //   }

  //   const decoded = parseJwt(userInfo.access_token);
  //   const userId = decoded?.user_id;

  //   if (userId) {
  //     dispatch(dealerAccess(userInfo.access_token, userId)).then((result) => {
  //       console.log("dealerAccess result:", result); // ✅ should now show data
  //     });
  //   }

  // }, [userInfo?.access_token]);

  // In the useEffect inside DashboardLayout
useEffect(() => {
  if (!userInfo?.access_token) {
    navigate("/login");
    return;
  }

  if (!dashboardData) {
    dispatch(fetchDashboardData(userInfo.access_token));
  }

  // ✅ Skip dealerAccess for customers (role 4) and salesmen (role 2)
  if (roleId === "4" || roleId === "2") return;

  const decoded = parseJwt(userInfo.access_token);
  const userId = decoded?.user_id;

  if (userId) {
    dispatch(dealerAccess(userInfo.access_token, userId));
  }
}, [userInfo?.access_token]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownOpen && !event.target.closest(".user-dropdown-container")) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest("aside") && !event.target.closest(".mobile-menu-button")) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser(userInfo.access_token));
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const menuRoutes = {
    Dashboard: "/dashboard",
    "Coupon Orders": "/dashboard/orders",
    "View Order Requests": "/dashboard/orderApprove",
    "Dealers": "/dashboard/UserList",
    "View Customers list": "/dashboard/customerList",
    "View Coupons statistics": "/dashboard/couponStat",
    Slogans: "/dashboard/sloganlist",
    "Access Modules": "/dashboard/access-module",
    Reports: "/dashboard/stateReport",
    "State Wise Report": "/dashboard/stateReport",
    // "City Wise Report": "/reports/city-wise",
    "Customer Wise Report": "/reports/customer-wise",
    "Admin Details": "/dashboard/profile",
    "Salesmen List": "/dashboard/salesmanlist",
    "Create Saleman": "/dashboard/createsalesman",
    "Gift Masters": "/dashboard/giftList",
    "View Customers": "/dashboard/salesmancustomerlist",
    "Scan QR": "/dashboard/salesmanconsumerqr",
    "Redemption List": "/dashboard/redemption-customers",
    Banners: "/dashboard/bannerlist",
    "Gift Offers": "/dashboard/offers",
    Offers: "/dashboard/offers",
    "Gifts Allotments": "/dashboard/giftList",
    "QR Designs": "/dashboard/qrdesignlist",
    Overview: "/dashboard/salesman-overview",
    "Gifts Schemes": "/dashboard/customer-gifts-schemes",
    "Update Password": null,
  };

  // const subMenus = { Reports: [] };
  // if (roleId === "1" || roleId === 1) {
  //   subMenus.Reports = [
  //     { name: "Salesman Consolidated Report", path: "/dashboard/salesmanReport" },
  //     { name: "Customer Consolidated Report", path: "/dashboard/customerReport" },
  //   ];
  // } else if (roleId === "6" || roleId === "06") {
  //   subMenus.Reports = [
  //     { name: "State Wise Report", path: "/dashboard/stateReport" },
  //     // { name: "City Wise Report", path: "/dashboard/cityReport" },
  //     { name: "Customers Reports", path: "/dashboard/customerReport" },
  //     { name: "Clients Reports", path: "/dashboard/clientReport" },
  //   ];
  // } else {
  //   subMenus.Reports = [
  //     { name: "Reports", path: "/dashboard/stateReport" },
  //     // { name: "City Reports", path: "/dashboard/cityReport" },
  //     { name: "Customers Reports", path: "/dashboard/customerReport" },
  //   ];
  // }
  // if (
  //   (roleId === "6" || roleId === "06") &&
  //   !subMenus.Reports.some((item) => item.name === "Clients Reports")
  // ) {
  //   subMenus.Reports.push({ name: "Clients Reports", path: "/dashboard/clientReport" });
  // }

  const menuIcons = {
    Dashboard: <FiHome size={22} />,
    "Dealers": <FiUser size={22} />,
    "Access Modules": <FiAlertCircle size={22} />,
    "View Order Requests": <FiShoppingCart size={22} />,
    "View Coupons statistics": <FiBarChart2 size={22} />,
    Slogans: <FiShoppingCart size={22} />,
    Reports: <FiFileText size={22} />,
    "Salesmen List": <FiUser size={22} />,
    "Admin Details": <FiCreditCard size={22} />,
    "Coupon Orders": <FiPlusCircle size={22} />,
    Logout: <FiLogOut size={22} />,
    "Gift Masters": <FiGift size={22} />,
    "View Customers": <FiUser size={22} />,
    "Scan QR": <FiSearch size={22} />,
    "Redemption List": <FiFileText size={22} />,
    Banners: <FiImage size={22} />,
    "Gift Offers": <FiGift size={22} />,
    Offers: <FiImage size={22} />,
    "Gifts Allotments": <FiGift size={22} />,
    "QR Designs": <FiSearch size={22} />,
    Overview: <FiBarChart2 size={22} />,
    "Gifts Schemes": <FiGift size={22} />,
    "Update Password": <FiLock size={22} />,
  };

  const isSalesmanRole = roleId === "2";
  const isCustomerRole = roleId === "4";
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const getRouteForMenuItem = (menuItem) => {
    if (menuItem === "Update Password") return null;
    if (isCustomerRole && menuItem === "Offers") return "/dashboard/customer-offers";
    return menuRoutes[menuItem] || "/dashboard";
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* {!isSalesmanRole && ( */}
        <aside
          onMouseEnter={() => !window.matchMedia("(max-width: 768px)").matches && setSidebarOpen(true)}
          onMouseLeave={() => !window.matchMedia("(max-width: 768px)").matches && setSidebarOpen(false)}
          className={`bg-gradient-to-b from-amber-50 to-amber-100 border-r border-amber-200 text-gray-900 shadow-lg p-3 sm:p-4 flex-col space-y-3 sm:space-y-4 fixed h-full z-40 transition-all duration-300
            ${mobileMenuOpen ? "translate-x-0 flex" : "-translate-x-full md:translate-x-0 md:flex hidden"}
            ${sidebarOpen || mobileMenuOpen ? "w-64" : "w-0 md:w-16"}
            ${!mobileMenuOpen && !sidebarOpen ? "md:overflow-hidden" : ""}
          `}
        >
          {mobileMenuOpen && (
            <div className="flex justify-end mb-2 md:hidden">
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-amber-200/50 transition-colors text-gray-700">
                <FiX size={24} />
              </button>
            </div>
          )}

          {/* Logo */}
          <div className="mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-amber-300">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-sm sm:text-lg">ER</span>
              </div>
              {(sidebarOpen || mobileMenuOpen) && (
                <h1 className="text-sm sm:text-lg font-extrabold tracking-tight">
                  <span style={{ backgroundImage: "linear-gradient(to right, #D97706, #F59E0B)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }} className="block">Easy</span>
                  <span style={{ backgroundImage: "linear-gradient(to right, #F59E0B, #D97706)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }} className="block">Rewardz</span>
                </h1>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex flex-col space-y-4 flex-1 overflow-y-auto">
            {menuList
              .filter((menuItem) => menuItem !== "Logout")
              .map((menuItem) => {
                if (menuItem === "Update Password") {
                  return (
                    <button
                      key={menuItem}
                      onClick={() => { setShowUpdatePasswordModal(true); setMobileMenuOpen(false); }}
                      className="flex items-center space-x-2 px-2 py-1 rounded hover:bg-amber-200/50 transition-colors text-gray-800 w-full text-left"
                    >
                      <span className="text-amber-600">{menuIcons[menuItem] || <FiSettings size={20} />}</span>
                      {(sidebarOpen || mobileMenuOpen) && <span className="font-medium text-xs sm:text-sm">{menuItem}</span>}
                    </button>
                  );
                }

                // if (subMenus[menuItem]) {
                //   return (
                //     <div key={menuItem} className="relative group">
                //       <div className="flex items-center justify-between w-full px-2 py-1 rounded hover:bg-amber-200/50 cursor-pointer transition-colors">
                //         <div className="flex items-center space-x-2">
                //           <span className="text-amber-600">{menuIcons[menuItem] || <FiSettings size={20} />}</span>
                //           {(sidebarOpen || mobileMenuOpen) && <span className="font-medium text-xs sm:text-sm text-gray-800">{menuItem}</span>}
                //         </div>
                //         {(sidebarOpen || mobileMenuOpen) && <span className="text-gray-600">▸</span>}
                //       </div>
                //       {(sidebarOpen || mobileMenuOpen) && (
                //         <div className="ml-6 mt-1 space-y-1 hidden group-hover:block">
                //           {subMenus[menuItem].map((subItem) => (
                //             <NavLink
                //               key={subItem.name}
                //               to={subItem.path}
                //               onClick={() => setMobileMenuOpen(false)}
                //               className={({ isActive }) =>
                //                 `block px-2 py-1 text-sm rounded hover:bg-amber-200/50 transition-colors ${isActive ? "bg-amber-500 text-amber font-medium" : "text-gray-800"}`
                //               }
                //             >
                //               {subItem.name}
                //             </NavLink>
                //           ))}
                //         </div>
                //       )}
                //     </div>
                //   );
                // }

                const route = getRouteForMenuItem(menuItem);
                if (!route) return null;

                return (
                  <NavLink
                    key={menuItem}
                    to={route}
                    end={menuItem === "Dashboard"}
                    onClick={() => setMobileMenuOpen(false)}
                    className={() => {
                      let shouldHighlight = false;
                      if (menuItem === "Dashboard") shouldHighlight = location.pathname === "/dashboard";
                      else if (isCustomerRole && menuItem === "Offers") shouldHighlight = location.pathname.startsWith("/dashboard/customer-offers");
                      else if (menuItem === "Gift Offers" || menuItem === "Offers") shouldHighlight = location.pathname.startsWith("/dashboard/offers");
                      else if (route !== "/dashboard") shouldHighlight = location.pathname.startsWith(route);
                      return `flex items-center space-x-2 px-2 py-1 rounded hover:bg-amber-200/50 transition-colors ${shouldHighlight ? "bg-amber-500 text-amber font-semibold" : "text-gray-800"}`;
                    }}
                  >
                    {() => {
                      let shouldHighlight = false;
                      if (menuItem === "Dashboard") shouldHighlight = location.pathname === "/dashboard";
                      else if (isCustomerRole && menuItem === "Offers") shouldHighlight = location.pathname.startsWith("/dashboard/customer-offers");
                      else if (menuItem === "Gift Offers" || menuItem === "Offers") shouldHighlight = location.pathname.startsWith("/dashboard/offers");
                      else if (route !== "/dashboard") shouldHighlight = location.pathname.startsWith(route);
                      return (
                        <>
                          <span className={shouldHighlight ? "text-amber" : "text-amber-600"}>{menuIcons[menuItem] || <FiSettings size={20} />}</span>
                          {(sidebarOpen || mobileMenuOpen) && <span className="font-medium text-xs sm:text-sm">{menuItem}</span>}
                        </>
                      );
                    }}
                  </NavLink>
                );
              })}
          </nav>

          {/* Logout */}
          <div className="mt-auto pt-3 sm:pt-4 border-t border-amber-300">
            <button onClick={handleLogout} className="flex items-center space-x-2 w-full px-2 py-2 rounded hover:bg-red-100 transition-colors text-red-600 hover:text-red-700">
              <span className="text-red-600"><FiLogOut size={20} /></span>
              {(sidebarOpen || mobileMenuOpen) && <span className="font-medium text-xs sm:text-sm">Logout</span>}
            </button>
          </div>

          {/* Footer */}
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-amber-300">
            {sidebarOpen || mobileMenuOpen ? (
              <div className="text-center">
                <p className="text-[10px] sm:text-xs text-gray-600 leading-tight">© {new Date().getFullYear()} MWB Technologies Pvt. Ltd.</p>
                <p className="text-[10px] sm:text-xs text-gray-600 mt-1">All rights reserved</p>
              </div>
            ) : (
              <div className="flex justify-center"><span className="text-[10px] sm:text-xs text-gray-600">©</span></div>
            )}
          </div>
        </aside>
      {/* )} */}

      {/* Main Content */}
      {/* <div className={`flex-1 flex flex-col transition-all duration-300 ${!isSalesmanRole ? "md:ml-16" : ""}`}>
         */}
         <div className="flex-1 flex flex-col transition-all duration-300 md:ml-16">
        {/* Header */}
        <header className="flex items-center justify-between bg-white text-gray-900 px-3 sm:px-4 md:px-6 py-3 sm:py-4 shadow-sm border-b border-gray-200">
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* {!isSalesmanRole && (
              <button onClick={toggleMobileMenu} className="mobile-menu-button md:hidden p-2 rounded-lg hover:bg-amber-50 transition-colors">
                <FiMenu size={24} className="text-gray-700" />
              </button>
            )} */}

             
              <button onClick={toggleMobileMenu} className="mobile-menu-button md:hidden p-2 rounded-lg hover:bg-amber-50 transition-colors">
                <FiMenu size={24} className="text-gray-700" />
              </button>
          
            <h2 className="text-gray-900 text-base sm:text-lg md:text-xl font-bold truncate">{firmName}</h2>
          </div>
             {(userInfo?.user_type === "promotional" ||
            dashboardData?.customer_login_type === "promotional") && (
            <button
              onClick={() => navigate("/dashboard/salesmanconsumerqr", { state: { mode: "customer" } })}
              className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg transition-all duration-200 border border-amber-400"
            >
              <FiSearch size={16} className="flex-shrink-0" />
              <span className="hidden sm:inline">Scan QR</span>
            </button>
          )}

          <div className="relative user-dropdown-container">
            <button
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-amber-50 transition-colors border border-amber-200"
            >
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-xs sm:text-sm">{userDetails?.name?.charAt(0)?.toUpperCase() || "U"}</span>
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate max-w-[100px] md:max-w-none">{userDetails?.name || "User"}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">
                  {userDetails?.user_type || (roleId === "6" || roleId === "06" ? "Admin" : roleId === "2" ? "Salesman" : roleId === "4" ? "Customer" : "Dealer")}
                </p>
              </div>
              <span className={`text-gray-600 transition-transform hidden sm:inline ${userDropdownOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {userDropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 sm:w-72 bg-white rounded-lg shadow-xl border border-amber-200 z-50 overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 text-white">
                  <p className="font-bold text-lg">{userDetails?.name || "User"}</p>
                  <p className="text-sm text-amber-100">
                    {userDetails?.user_type || (roleId === "6" || roleId === "06" ? "Admin" : roleId === "2" ? "Salesman" : roleId === "4" ? "Customer" : "Dealer")}
                  </p>
                </div>
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                  {userDetails?.firm_name && <div><p className="text-xs font-semibold text-gray-500 uppercase">Firm Name</p><p className="text-sm text-gray-900 font-medium">{userDetails.firm_name}</p></div>}
                  {userDetails?.mobile && <div><p className="text-xs font-semibold text-gray-500 uppercase">Mobile</p><p className="text-sm text-gray-900">{userDetails.mobile}</p></div>}
                  {userDetails?.email && <div><p className="text-xs font-semibold text-gray-500 uppercase">Email</p><p className="text-sm text-gray-900 break-words">{userDetails.email}</p></div>}
                  {userDetails?.address && <div><p className="text-xs font-semibold text-gray-500 uppercase">Address</p><p className="text-sm text-gray-900">{userDetails.address}</p></div>}
                  <div className="grid grid-cols-2 gap-3">
                    {userDetails?.city && <div><p className="text-xs font-semibold text-gray-500 uppercase">City</p><p className="text-sm text-gray-900">{userDetails.city}</p></div>}
                    {userDetails?.state && <div><p className="text-xs font-semibold text-gray-500 uppercase">State</p><p className="text-sm text-gray-900">{userDetails.state}</p></div>}
                  </div>
                  {userDetails?.pincode && <div><p className="text-xs font-semibold text-gray-500 uppercase">Pincode</p><p className="text-sm text-gray-900">{userDetails.pincode}</p></div>}
                </div>
                <div className="border-t border-gray-200 p-2">
                  <button onClick={handleLogout} className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium">
                    <FiLogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="relative flex-1 overflow-y-auto px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 pb-6 sm:pb-10">
          <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-white via-amber-50 to-amber-100"></div>
            <div className="absolute top-20 left-20 w-32 h-32 bg-amber-300/15 rounded-full blur-3xl animate-bounce" style={{ animationDuration: "6s" }}></div>
            <div className="absolute bottom-20 right-20 w-40 h-40 bg-amber-400/15 rounded-full blur-3xl animate-bounce" style={{ animationDuration: "8s", animationDelay: "1s" }}></div>
            <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-amber-500/15 rounded-full blur-2xl animate-pulse" style={{ animationDuration: "4s" }}></div>
            <div className="absolute bottom-1/4 right-1/3 w-36 h-36 bg-amber-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDuration: "5s", animationDelay: "2s" }}></div>
            <div className="absolute top-1/3 right-1/4 w-28 h-28 bg-amber-400/10 rounded-full blur-2xl animate-bounce" style={{ animationDuration: "7s", animationDelay: "0.5s" }}></div>
            <div className="absolute bottom-1/3 left-1/3 w-44 h-44 bg-amber-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: "6s", animationDelay: "1.5s" }}></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/3 to-transparent animate-pulse" style={{ animationDuration: "3s" }}></div>
          </div>

          {/* ✅ Wrap children with PermissionContext so child pages can use permissions */}
          {/* <PermissionContext.Provider value={{ permissions: menuPermissions, permissionsLoaded }}>
  <div className="relative z-10">{children}</div>
</PermissionContext.Provider> */}

          <PermissionContext.Provider value={{ permissions: menuPermissions, permissionsLoaded }}>
            <div className="relative z-10">
              {/* {permissionsLoaded ? children : (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin" />
                </div>
              )} */}
             
{(permissionsLoaded || roleId === "4" || roleId === "2") ? children : (
  <div className="flex items-center justify-center py-20">
    <div className="w-8 h-8 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin" />
  </div>
)}
            </div>
          </PermissionContext.Provider>
        </main>
      </div>

      <UpdatePasswordModal isOpen={showUpdatePasswordModal} onClose={() => setShowUpdatePasswordModal(false)} />
    </div>
  );
};

export default DashboardLayout;